import { Component, Input, OnInit, OnChanges, SimpleChanges, signal } from '@angular/core';
import { EconomyService } from '../../services/economy.service';
import { PriceHistoryEntry, PricedItem } from '../../models/economy-models';
import { generateEnhancedValueHistoryPath, PathSegment } from './value-history-path.helper';

@Component({
  selector: 'app-value-history',
  imports: [],
  templateUrl: './value-history.component.html',
  styleUrl: './value-history.component.scss'
})
export class ValueHistoryComponent implements OnInit, OnChanges {
  @Input() name!: string;
  @Input() unique: boolean = false;

  history = signal<PriceHistoryEntry[] | undefined>(undefined);
  previousItem = signal<PricedItem | undefined>(undefined);
  percentageChange = signal<number | undefined>(undefined);
  isPositive = signal<boolean>(false);
  pathSegments = signal<PathSegment[]>([]);

  readonly SVG_WIDTH = 60;
  readonly SVG_HEIGHT = 20;
  private readonly SVG_PADDING = 2;

  constructor(private economyService: EconomyService) {}

  ngOnInit(): void {
    this.updateData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['name'] || changes['unique']) {
      this.updateData();
    }
  }

  private updateData(): void {
    if (!this.name) {
      this.history.set(undefined);
      this.previousItem.set(undefined);
      return;
    }

    const item = this.unique
      ? this.economyService.getUniqueValue(this.name)
      : this.economyService.getCurrencyValue(this.name);

    if (!item || !item.history || item.history.length < 2) {
      this.history.set(undefined);
      this.previousItem.set(undefined);
      return;
    }

    // Load previous value
    const previousItem = this.unique
      ? this.economyService.getPreviousUniqueValue(this.name)
      : this.economyService.getPreviousCurrencyValue(this.name);

    // For uniques, replace the most recent history entry with the current price
    // This ensures the graph and percentage calculations use the same weighted average price
    let historyToUse = item.history;
    if (this.unique && item.price !== undefined && item.history.length > 0) {
      const mostRecentEntry = item.history[0];
      historyToUse = [
        {
          ...mostRecentEntry,
          price: item.price,
          type: item.type,
        },
        ...item.history.slice(1)
      ];
    }

    const effectivePreviousItem = this.resolvePreviousItem(previousItem, historyToUse);

    this.history.set(historyToUse);
    this.previousItem.set(effectivePreviousItem);
    this.calculatePercentageChange(historyToUse, effectivePreviousItem);
    this.updateSvgPath(historyToUse, effectivePreviousItem);
  }

  // Ignore previous snapshot when it is from the same import as the latest history entry.
  // In that case the trendline falls back to oldest→newest history, so the percentage must too.
  private resolvePreviousItem(previousItem: PricedItem | undefined, history: PriceHistoryEntry[]): PricedItem | undefined {
    if (!previousItem || history.length === 0) return undefined;

    const previousTs = new Date(previousItem.ts).getTime();
    const mostRecentHistoryTs = new Date(history[0].ts).getTime();
    if (previousTs >= mostRecentHistoryTs) return undefined;

    return previousItem;
  }

  private calculatePercentageChange(history: PriceHistoryEntry[], previousItem?: PricedItem): void {
    if (history.length < 2) {
      this.percentageChange.set(undefined);
      return;
    }

    // First entry (index 0) = most recent price (current value)
    const mostRecentPrice = history[0].price;

    // Use previous import when available, otherwise use oldest history price
    let basePrice: number;
    if (previousItem && previousItem.price > 0) {
      basePrice = previousItem.price;
    } else {
      const oldestPrice = history[history.length - 1].price;
      if (oldestPrice === 0) {
        this.percentageChange.set(undefined);
        return;
      }
      basePrice = oldestPrice;
    }

    if (basePrice === 0) {
      this.percentageChange.set(undefined);
      return;
    }

    // Calculate percentage change from previous (or oldest) to current (most recent)
    const change = ((mostRecentPrice - basePrice) / basePrice) * 100;
    this.percentageChange.set(change);
    this.isPositive.set(change >= 0);
  }

  private updateSvgPath(history: PriceHistoryEntry[], previousItem?: PricedItem): void {
    const segments = generateEnhancedValueHistoryPath(history, {
      width: this.SVG_WIDTH,
      height: this.SVG_HEIGHT,
      padding: this.SVG_PADDING,
      previousValue: previousItem ? {
        price: previousItem.price,
        ts: previousItem.ts
      } : undefined,
      isUnique: this.unique
    });
    this.pathSegments.set(segments);
  }

  getFormattedPercentage(): string {
    const change = this.percentageChange();
    if (change === undefined) return '';

    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  }

  getColor(): string {
    return this.isPositive() ? '#397439' : '#b12020';
  }

  getSegmentColor(color: 'grey' | 'green' | 'red'): string {
    switch (color) {
      case 'grey':
        return '#888888';
      case 'green':
        return '#397439';
      case 'red':
        return '#b12020';
      default:
        return '#397439';
    }
  }
}
